import { formatCurrency, formatMdd } from '@apply/ui';
import {
    ButtonTheme,
    Panel,
    PanelBorderRadius,
    PanelTheme,
    Tab,
    TabKeyIdentifierPrefix,
    TabPanel,
    TabPanelTheme,
} from '@apply/components';
import { GenericPopup } from '@apply/components';
import { IDrawHistory } from '@apply/api/src/api/DrawApi';
import '../../../../../../../apps/bfi-apply/src/app/draws_v2/scss/_drawsummary.scss';
import { useDeriveDrawBudgetDataFromDrawBudgetId } from '../../../../../../../apps/bfi-apply/src/app/home_v2/hooks/useDeriveDrawBudgetDataFromDrawBudgetId';
import { MobileDrawHistoryEntry } from '../../../../../../../apps/bfi-apply/src/app/draws_v2/DrawSummary/components/MobileDrawControl/components/MobileDrawDetails/components/MobileDrawBudgetItem/components/MobileDrawHistory/components/MobileDrawHistoryEntry';

export interface IDrawSummaryPopupProps {
    drawBudgetId: number;
    drawHistoryLines: IDrawHistory[];
    open: boolean;
    handleCloseEvent: () => void;
    tabShown: 'available' | 'funded';
}

export const DrawSummaryPopup = ({
    open,
    handleCloseEvent,
    drawBudgetId,
    drawHistoryLines,
    tabShown,
}: IDrawSummaryPopupProps): JSX.Element => {
    const { availableLines, totalAvailable } = useDeriveDrawBudgetDataFromDrawBudgetId(drawBudgetId);

    const handleClose = () => {
        handleCloseEvent();
    };

    const actionButtons = [
        {
            id: 'closeButton',
            theme: ButtonTheme.Primary,
            children: 'Close',
            width: '70%',
            handleClick: handleClose,
        },
    ];

    const drawSelectorDialog = (
        <>
            <div className='drawsummary'>
                <TabPanel
                    id='drawSummaryTabPanel'
                    theme={TabPanelTheme.Whiteout}
                    defaultIndex={tabShown === 'available' ? 1 : 0}
                    width='100%'
                >
                    <Tab
                        id='drawSummaryFundedTab'
                        text='Funded'
                        key={`${TabKeyIdentifierPrefix}Apply`}
                    >
                        {drawHistoryLines.map((entry, i: number) => (
                            <MobileDrawHistoryEntry
                                key={`entry-drawNumber${i}`}
                                item={`Draw #${i} - ${formatMdd(entry.fundedOn)}`}
                                amount={formatCurrency(entry.amountFunded)}
                            />
                        ))}
                    </Tab>
                    <Tab
                        id='drawSummaryAvailableTab'
                        text='Available'
                        key={`${TabKeyIdentifierPrefix}Loans`}
                    >
                        <Panel
                            theme={PanelTheme.White}
                            borderRadius={PanelBorderRadius.Medium}
                            padding={8}
                        >
                            <div className='body'>
                                {availableLines.map((dbi) => (
                                    <div
                                        key={dbi.label}
                                        className='item'
                                    >
                                        <span className='label'>{dbi.label}</span>
                                        <span className='value'>
                                            {dbi.value.toLocaleString('en-US', {
                                                style: 'currency',
                                                currency: 'USD',
                                            })}
                                        </span>
                                    </div>
                                ))}
                            </div>
                            <div className='total'>
                                <span className='label'>Total Draw</span>
                                <span className='value'>
                                    {totalAvailable.toLocaleString('en-US', {
                                        style: 'currency',
                                        currency: 'USD',
                                    })}
                                </span>
                            </div>
                        </Panel>
                    </Tab>
                </TabPanel>
            </div>
        </>
    );

    return (
        <>
            <GenericPopup
                id='drawRequestPopup'
                isOpen={open}
                actionButtons={actionButtons}
                handleClose={handleClose}
                aria-labelledby='alert-dialog-title'
                aria-describedby='alert-dialog-description'
                hideOnOutsideClick={true}
                showCloseButton={true}
                showTitle={true}
                title='Draw Summary'
                width='550px'
                maxWidth='90%'
                maxHeight='95%'
                buttonAlignment='center'
                wrapTitle={true}
            >
                {drawSelectorDialog}
            </GenericPopup>
        </>
    );
};
